import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Twitter } from 'lucide-react';
import { useTheme } from './ThemeContext';
import StockService from '../Api_Module/Api_Services/StockService';
import { toast } from 'react-toastify';

interface FooterProps {
  openPopup?: () => void;
}
export function Footer({ openPopup }: FooterProps) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await StockService.subscribeNewsletter(email);
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-theme-primary text-theme-primary py-12 border-t border-theme-secondary">
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-16 ">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* SECTIONS */}
          <div>
            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-4">SECTIONS</h3>
            <ul className="space-y-3">
              <li><Link to="/fund-insights" className="text-theme-secondary hover:text-theme-primary transition-colors">Fund Insights</Link></li>
              <li><Link to="/market-movers" className="text-theme-secondary hover:text-theme-primary transition-colors">Market Movers</Link></li>
              <li><Link to="/chart-analysis" className="text-theme-secondary hover:text-theme-primary transition-colors">Chart Analysis</Link></li>
              <li><Link to="/screener" className="text-theme-secondary hover:text-theme-primary transition-colors">Screener</Link></li>
              <li><Link to="/compare" className="text-theme-secondary hover:text-theme-primary transition-colors">Compare Stocks</Link></li>
              <li><Link to="/news" className="text-theme-secondary hover:text-theme-primary transition-colors">News</Link></li>
            </ul>
          </div>

          {/* SERVICES */}
          <div>
            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-4">SERVICES</h3>
            <ul className="space-y-3">
              <li><Link to="/pricing" className="text-theme-secondary hover:text-theme-primary transition-colors">Stock Fortress Pro</Link></li>
              <li><Link to="/pricing" className="text-theme-secondary hover:text-theme-primary transition-colors">Stock Forecast</Link></li>
              <li><Link to="/pricing" className="text-theme-secondary hover:text-theme-primary transition-colors">SWOT Analysis</Link></li>
              <li><Link to="/newsletter" className="text-theme-secondary hover:text-theme-primary transition-colors">Free Newsletter</Link></li>
            </ul>
          </div>

          {/* WEBSITE */}
          <div>
            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-4">WEBSITE</h3>
            <ul className="space-y-3">
              <li><Link onClick={() => { if (openPopup) openPopup(); else navigate('/login'); }} className="text-theme-secondary hover:text-theme-primary transition-colors">Login</Link></li>
              <li><Link  onClick={() => { if (openPopup) openPopup(); else navigate('/login'); }} className="text-theme-secondary hover:text-theme-primary transition-colors">Create Account</Link></li>
              <li><Link to="/sitemap" className="text-theme-secondary hover:text-theme-primary transition-colors">Sitemap</Link></li>
              {/* <li><Link to="/advertise" className="text-theme-secondary hover:text-theme-primary transition-colors">Advertise</Link></li>
              <li><Link to="/faq" className="text-theme-secondary hover:text-theme-primary transition-colors">FAQ</Link></li> */}
            </ul>
          </div>

          {/* COMPANY */}
          <div>
            <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-4">COMPANY</h3>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-theme-secondary hover:text-theme-primary transition-colors">About</Link></li>
              <li><Link to="/about" className="text-theme-secondary hover:text-theme-primary transition-colors">Contact Us</Link></li>
              <li><Link to="/terms-of-service" className="text-theme-secondary hover:text-theme-primary transition-colors">Terms of Use</Link></li>
              <li><Link to="/privacy-policy" className="text-theme-secondary hover:text-theme-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/disclaimer" className="text-theme-secondary hover:text-theme-primary transition-colors">Data Disclaimer</Link></li>
              {/* <li><Link to="/affiliate" className="text-theme-secondary hover:text-theme-primary transition-colors">Affiliate Program</Link></li> */}
            </ul>
          </div>

          {/* NEWSLETTER & THEME */}
          <div className="md:col-span-2 lg:col-span-1">
            <div>
              <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-3">MARKET NEWSLETTER</h3>
              <p className="text-sm text-theme-muted mb-3">Daily market news in bullet point format.</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email" 
                  className="flex-1 px-3 py-2 bg-white text-gray-900 rounded-md min-w-0"
                  disabled={loading}
                />
                <button 
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 rounded-md font-medium text-sm btn active"
                >
                  {loading ? '...' : 'Subscribe'}
                </button>
              </form>
            </div>

            {/* <div className="mt-6">
              <h3 className="text-sm font-semibold text-theme-muted uppercase tracking-wider mb-3">SITE THEME</h3>
              <div className="flex gap-2">
                <button 
                  onClick={toggleTheme}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-colors text-sm ${
                    theme === 'light' ? 'bg-blue-600 text-theme-primary' : 'bg-theme-secondary text-theme-secondary hover:bg-theme-tertiary'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Light
                </button>
                <button 
                  onClick={toggleTheme}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-colors text-sm ${
                    theme === 'dark' ? 'bg-blue-600 text-theme-primary' : 'bg-theme-secondary text-theme-secondary hover:bg-theme-tertiary'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  Dark
                </button>
              </div>
            </div> */}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 mt-8 border-t border-theme-secondary flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-theme-muted text-center md:text-left">© 2025 Stock Fortress. All rights reserved.</p>
          <div className="flex gap-4">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-theme-muted hover:text-theme-primary transition-colors">
              <Facebook className="w-6 h-6" />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-theme-muted hover:text-theme-primary transition-colors">
              <Twitter className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}